import type { CaseFilter, CaseFilterEditing } from "@/queries/useFetchCaseFiltersets";
import { Box, Button, Modal, Stack, TextField, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Check, X } from "lucide-react";
import React, { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import AccidentFields from "./AdvancedFilters/AccidentFields";
import DateRangeFields from "./AdvancedFilters/DateRangeFields";
import FilterFields from "./AdvancedFilters/FilterFields";

const ModalContent = styled(Box)(({ theme }) => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 1000,
    maxHeight: "90vh",
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[5],
    display: "flex",
    flexDirection: "column",
}));

const ContentArea = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4),
    overflow: "auto",
    maxHeight: "calc(90vh - 140px)",
}));

const FilterSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(4),
}));

const ModalFooter = styled(Box)(({ theme }) => ({
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 4),
    display: "flex",
    justifyContent: "flex-end",
    backgroundColor: theme.palette.background.paper,
}));

const SaveFilterInput = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    width: "100%",
}));

interface AdvancedFiltersModalProps {
    open: boolean;
    onClose: () => void;
    onApply: (filters: CaseFilter) => void;
    onSaveFilter?: (name: string, filters: CaseFilterEditing) => void;
    editingFilter?: CaseFilter | null;
    saveSuccessful: boolean | null;
    setSaveSuccessful: (saveSuccessful: boolean | null) => void;
}

type FormValues = CaseFilterEditing & {
    name: string;
};

const defaultValues: FormValues = {
    idUUID: null,
    id: "",
    name: "",

    caseNumber: "",
    clientName: "",
    caseType: [],
    incidentState: [],
    createdBy: [],

    createdDateFrom: null,
    createdDateTo: null,

    doiFrom: null,
    doiTo: null,

    solFrom: null,
    solTo: null,
    anteLitemCase: [],
    governmentEntity: [],

    anteLitemSentFrom: null,
    anteLitemSentTo: null,
    liabilityStatus: [],
    liabilityAccepted: [],

    lbtyDemandSentFrom: null,
    lbtyDemandSentTo: null,
    policyLimits: null,
    medpay: null,

    erisaDateSentFrom: null,
    erisaDateSentTo: null,
    idCaseStatusUM: [],
    uMUIMAccepted: [],

    umDemandSentFrom: null,
    umDemandSentTo: null,
    assignedTeam: [],
    assignedToAttorney: null,
    assignedToDisbursementSpecialist: null,
    assignedToIntakeSpecialist: null,
    assignedToParalegal: null,
    assignedToMedicalRecordsClerk: null,
    financeLoan: null,
    attorneyFee: null,
    crmStatus: null,
    litigationSubStatus: null,

    accidentReportDone: [],
    pdSettled: [],
    primaryStatute: [],
};

const AdvancedFiltersModal: React.FC<AdvancedFiltersModalProps> = ({
    open,
    onClose,
    onApply,
    onSaveFilter,
    editingFilter,
    saveSuccessful,
    setSaveSuccessful,
}) => {
    const form = useForm<FormValues>({ defaultValues });
    const { register, handleSubmit, reset, formState } = form;
    const [isSaving, setIsSaving] = React.useState(false);

    useEffect(() => {
        if (open) {
            if (editingFilter) {
                reset({ ...defaultValues, ...editingFilter });
            } else {
                reset(defaultValues);
            }
        }
    }, [editingFilter, open, reset]);

    const handleApply = (data: FormValues) => {
        onApply(data as CaseFilter);
    };

    const clearForm = () => {
        reset({
            ...defaultValues,
            id: editingFilter?.id ?? "",
            idUUID: editingFilter?.idUUID ?? "",
            name: editingFilter?.name ?? "",
        });
    };

    const handleSaveFilter = handleSubmit((data) => {
        if (onSaveFilter) {
            onSaveFilter(data.name.trim(), data);
        }
    });

    const handleCancelSave = () => {
        setIsSaving(false);
        reset({ ...form.getValues(), name: "" });
    };

    useEffect(() => {
        if (saveSuccessful) {
            setIsSaving(false);
            reset({ ...form.getValues(), name: "" });
            onClose();
        }

        if (saveSuccessful !== null) {
            setSaveSuccessful(null);
        }
    }, [saveSuccessful]);

    return (
        <Modal open={open} onClose={onClose}>
            <ModalContent>
                <FormProvider {...form}>
                    <ContentArea>
                        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2 }}>
                            {editingFilter?.idUUID
                                ? `Edit Filter: ${editingFilter?.name}`
                                : "Advanced Filters"}
                        </Typography>

                        <FilterSection>
                            <FilterFields />
                        </FilterSection>

                        <FilterSection>
                            <DateRangeFields />
                        </FilterSection>

                        <FilterSection>
                            <AccidentFields />
                        </FilterSection>
                    </ContentArea>

                    <ModalFooter>
                        {editingFilter?.idUUID ? (
                            <Stack
                                sx={{
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                    width: "100%",
                                }}
                            >
                                <Button
                                    onClick={clearForm}
                                    color="warning"
                                    variant="outlined"
                                    sx={{ mr: 2 }}
                                >
                                    Clear Filters
                                </Button>
                                <Box>
                                    <Button onClick={onClose} color="inherit" sx={{ mr: 2 }}>
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleSubmit(handleApply)}
                                        variant="contained"
                                        color="primary"
                                    >
                                        Save Changes
                                    </Button>
                                </Box>
                            </Stack>
                        ) : isSaving ? (
                            <SaveFilterInput>
                                <TextField
                                    autoFocus
                                    placeholder="Enter filter name"
                                    fullWidth
                                    {...register("name", {
                                        required: "Filter name is required",
                                    })}
                                    error={!!formState.errors.name}
                                    helperText={formState.errors.name?.message}
                                />
                                <Button
                                    onClick={handleSaveFilter}
                                    color="primary"
                                    startIcon={<Check size={18} />}
                                >
                                    Save
                                </Button>
                                <Button
                                    onClick={handleCancelSave}
                                    color="inherit"
                                    startIcon={<X size={18} />}
                                >
                                    Cancel
                                </Button>
                            </SaveFilterInput>
                        ) : (
                            <Stack
                                sx={{
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                    width: "100%",
                                }}
                            >
                                <Box>
                                    <Button
                                        onClick={clearForm}
                                        color="warning"
                                        variant="outlined"
                                        sx={{ mr: 2 }}
                                    >
                                        Clear Filters
                                    </Button>
                                </Box>
                                <Box>
                                    <Button onClick={onClose} color="inherit" sx={{ mr: 2 }}>
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={() => setIsSaving(true)}
                                        color="primary"
                                        variant="outlined"
                                        sx={{ mr: 2 }}
                                    >
                                        Save Current
                                    </Button>
                                    <Button
                                        onClick={handleSubmit(handleApply)}
                                        variant="contained"
                                        color="primary"
                                    >
                                        Apply Filters
                                    </Button>
                                </Box>
                            </Stack>
                        )}
                    </ModalFooter>
                </FormProvider>
            </ModalContent>
        </Modal>
    );
};

export default AdvancedFiltersModal;
